import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Home, Wallet, Users, ArrowUpCircle, ArrowDownCircle, History, Shield, Settings, Bell, Copy, RefreshCw, HelpCircle, LogOut } from "lucide-react";

const CryptoIcon = ({ symbol }) => {
  const icons = { BTC: { bg: '#F7931A', text: '₿' }, ETH: { bg: '#627EEA', text: 'Ξ' }, USDT: { bg: '#26A17B', text: 'T' }, RUB: { bg: '#4A5568', text: '₽' } };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: icon.bg }}>{icon.text}</div>;
};

export default function BitExchangeMain() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    if (themeParam) setBrowserTheme(themeParam);
    if (dataParam) { 
      try { 
        setData(JSON.parse(decodeURIComponent(dataParam))); 
      } catch (e) {
        console.error('Parse error:', e);
      } 
    }
    document.title = 'P2P Обмен | BitExchange';
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/bitexchange-favicon.svg';
  }, [searchParams]);

  if (!data) return <div className="min-h-screen bg-[#f0f4ff] flex items-center justify-center">Загрузка...</div>;

  const isDark = browserTheme === 'dark';
  const theme = { bg: isDark ? '#202124' : '#dee1e6', activeTabBg: isDark ? '#292a2d' : '#ffffff', inactiveTabBg: isDark ? '#35363a' : '#c9cdd4', addressBarBg: isDark ? '#292a2d' : '#ffffff', addressInputBg: isDark ? '#202124' : '#f1f3f4', textPrimary: isDark ? '#e8eaed' : '#202124', textSecondary: isDark ? '#9aa0a6' : '#5f6368', border: isDark ? '#3c4043' : '#dadce0' };
  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';
  const cryptoBalances = [
    { symbol: 'USDT', name: 'Tether', balance: data.usdtBalance || '0', rub: (parseFloat(data.usdtBalance || 0) * 92).toFixed(2) },
    { symbol: 'BTC', name: 'Bitcoin', balance: data.btcBalance || '0', rub: (parseFloat(data.btcBalance || 0) * 6500000).toFixed(2) },
    { symbol: 'ETH', name: 'Ethereum', balance: data.ethBalance || '0', rub: (parseFloat(data.ethBalance || 0) * 240000).toFixed(2) },
  ];
  const totalRub = cryptoBalances.reduce((sum, c) => sum + parseFloat(c.rub || 0), 0);
  const menuItems = [
    { icon: Home, label: 'Главная', active: true }, { icon: Users, label: 'P2P Торговля', active: false },
    { icon: Wallet, label: 'Кошелёк', active: false }, { icon: ArrowUpCircle, label: 'Депозит', active: false },
    { icon: ArrowDownCircle, label: 'Вывод', active: false }, { icon: History, label: 'История', active: false },
    { icon: Shield, label: 'Верификация', active: false }, { icon: Settings, label: 'Настройки', active: false },
  ];

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.bg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]">
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px] truncate" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/bitexchange-favicon.svg" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px] truncate" style={{ color: theme.textPrimary }}>P2P | BitExchange</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center ml-1 mb-1" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg></button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>bitexchange.ru</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col template-preview-scale" style={{ backgroundColor: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <BrowserFrame />
      <div className="flex-1 flex bg-[#f0f4ff]">
        <aside className="w-56 bg-white flex flex-col flex-shrink-0 border-r border-gray-200 shadow-sm">
          <div className="p-4"><img src="/bitexchange-logo.svg" alt="BitExchange" className="h-8" /></div>
          <nav className="flex-1 py-2">
            {menuItems.map((item, i) => { const Icon = item.icon; return (
              <a key={i} href="#" className={`flex items-center gap-3 px-4 py-2.5 text-sm ${item.active ? 'text-[#3B82F6] bg-[#3B82F6]/10' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Icon className="w-5 h-5" /><span>{item.label}</span>
              </a>
            ); })}
            <div className="mt-4 border-t pt-2">
              <a href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-50"><HelpCircle className="w-5 h-5" /><span>Поддержка</span></a>
              <a href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-50"><LogOut className="w-5 h-5" /><span>Выйти</span></a>
            </div>
          </nav>
        </aside>
        <div className="flex-1 flex flex-col min-w-0">
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">P2P Обмен</h1>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-400 relative"><Bell className="w-5 h-5" /><span className="absolute top-1 right-1 w-2 h-2 bg-[#3B82F6] rounded-full"></span></button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</span>
                </div>
                <span className="text-sm font-medium">{accountName}</span>
                <span className="text-xs text-gray-500">#{userId}</span>
                <Copy className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </header>
          <main className="flex-1 p-6 overflow-auto">
            <div className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div><p className="text-blue-200 text-sm">Общий баланс</p><p className="text-3xl font-bold">{totalRub.toLocaleString('ru-RU')} ₽</p></div>
                <button className="p-2 bg-white/10 rounded-lg hover:bg-white/20"><RefreshCw className="w-5 h-5" /></button>
              </div>
              <div className="flex gap-3">
                <button className="flex-1 py-2.5 bg-white text-[#3B82F6] rounded-lg font-medium flex items-center justify-center gap-2"><Users className="w-4 h-4" /> P2P Торговля</button>
                <button className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg font-medium flex items-center justify-center gap-2"><ArrowUpCircle className="w-4 h-4" /> Депозит</button>
                <button className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg font-medium flex items-center justify-center gap-2"><ArrowDownCircle className="w-4 h-4" /> Вывод</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-medium text-gray-900">Мои активы</h3></div>
                <div className="divide-y divide-gray-100">
                  {cryptoBalances.map(c => (
                    <div key={c.symbol} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50">
                      <div className="flex items-center gap-3"><CryptoIcon symbol={c.symbol} /><div><div className="font-medium text-gray-900">{c.symbol}</div><div className="text-xs text-gray-500">{c.name}</div></div></div>
                      <div className="text-right"><div className="font-medium text-gray-900">{c.balance}</div><div className="text-xs text-gray-500">≈ {parseFloat(c.rub).toLocaleString('ru-RU')} ₽</div></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-medium text-gray-900 mb-4">Верификация</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm text-gray-700">Email подтверждён</span>
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm text-gray-700">Телефон подтверждён</span>
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm text-gray-700">KYC пройден</span>
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
